import { Play } from "@phosphor-icons/react";
import { trackCoverUrl, usePlayer } from "@/context/PlayerContext";
import { Link } from "react-router-dom";

export function TrackCard({ track, list }) {
  const player = usePlayer();
  const cover = trackCoverUrl(track);
  const isPlaying = player?.current?.id === track.id && player.playing;

  return (
    <div
      className="group"
      data-testid={`track-card-${track.id}`}
    >
      <div className="relative aspect-square bg-neutral-900 border border-neutral-800 overflow-hidden">
        {cover ? (
          <img src={cover} alt={track.title} className="w-full h-full object-cover cover-hover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-5xl text-neutral-700">
            {track.title[0]}
          </div>
        )}
        <button
          onClick={() => player.playTrack(track, list)}
          className="absolute right-3 bottom-3 w-11 h-11 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all shadow-lg shadow-red-900/40"
          data-testid={`play-track-${track.id}`}
          aria-label={`Play ${track.title}`}
        >
          <Play size={16} weight="fill" />
        </button>
        {isPlaying && (
          <div className="absolute left-3 top-3 flex items-end h-5 text-red-500">
            <span className="wave-bar" style={{ height: 6 }} />
            <span className="wave-bar" style={{ height: 10 }} />
            <span className="wave-bar" style={{ height: 14 }} />
            <span className="wave-bar" style={{ height: 8 }} />
          </div>
        )}
      </div>
      <div className="mt-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/artist/${track.artist_id}`} className="block">
            <div className="font-display text-base font-bold leading-tight truncate hover:text-red-500 transition-colors" data-testid={`track-title-${track.id}`}>
              {track.title}
            </div>
          </Link>
          <div className="font-mono-ui text-[10px] uppercase tracking-widest text-neutral-400 truncate">
            {track.artist_name}
          </div>
        </div>
        <div className="font-mono-ui text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">
          {track.genre || "—"}
        </div>
      </div>
    </div>
  );
}

export function TrackRow({ track, index, list }) {
  const player = usePlayer();
  const isPlaying = player?.current?.id === track.id && player.playing;
  const cover = trackCoverUrl(track);
  return (
    <div
      className="track-row grid grid-cols-[auto_1fr_auto_auto] md:grid-cols-[auto_3fr_2fr_1fr_auto] items-center gap-4 px-4 py-3 border-b border-neutral-900 hover:bg-neutral-950 transition-colors"
      data-testid={`track-row-${track.id}`}
    >
      <div className="w-6 relative text-neutral-500 font-mono-ui text-xs tabular-nums">
        <span className="track-number">{String(index + 1).padStart(2, "0")}</span>
        <button
          className="play-overlay absolute inset-0 flex items-center justify-start text-white"
          onClick={() => player.playTrack(track, list)}
          data-testid={`row-play-${track.id}`}
          aria-label={`Play ${track.title}`}
        >
          <Play size={14} weight="fill" />
        </button>
      </div>
      <div className="flex items-center gap-3 min-w-0">
        {cover && <img src={cover} alt="" className="w-10 h-10 object-cover border border-neutral-900" />}
        <div className="min-w-0">
          <div className="font-display font-bold truncate flex items-center gap-2">
            {track.title}
            {isPlaying && <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />}
          </div>
          <Link to={`/artist/${track.artist_id}`} className="font-mono-ui text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white truncate block">
            {track.artist_name}
          </Link>
        </div>
      </div>
      <div className="hidden md:block font-mono-ui text-[11px] text-neutral-500 uppercase">{track.genre}</div>
      <div className="font-mono-ui text-[11px] text-neutral-500 tabular-nums">{track.plays?.toLocaleString?.() ?? 0} plays</div>
      <div className="font-mono-ui text-[11px] text-neutral-500 tabular-nums hidden md:block">{track.likes?.toLocaleString?.() ?? 0} ♥</div>
    </div>
  );
}
